import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SocialShareDialog - Share verse to social media platforms
 */
const SocialShareDialog = ({ isOpen, onClose, verse }) => {
  const { language } = useLanguage();

  if (!verse) return null;

  const shareText = `"${verse.text}"\n\n${verse.reference} (${verse.translation})`;
  const encodedText = encodeURIComponent(shareText);
  // Per-verse OG share page — unfurls with the actual verse on social platforms.
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = `${BACKEND_URL}/api/share/verse?ref=${encodeURIComponent(verse.reference)}&tr=${encodeURIComponent(verse.translation || 'KJV')}`;
  const encodedUrl = encodeURIComponent(shareUrl);

  const socialPlatforms = [
    {
      name: 'WhatsApp',
      icon: '💬',
      color: 'bg-green-500 hover:bg-green-600',
      url: `https://wa.me/?text=${encodedText}%0A${encodedUrl}`,
      available: true
    },
    {
      name: 'Facebook',
      icon: '📘',
      color: 'bg-blue-600 hover:bg-blue-700',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      available: true
    },
    {
      name: 'Twitter',
      icon: '🐦',
      color: 'bg-sky-500 hover:bg-sky-600',
      url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      available: true
    },
    {
      name: 'LinkedIn',
      icon: '💼',
      color: 'bg-blue-700 hover:bg-blue-800',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      available: true
    },
    {
      name: 'Instagram',
      icon: '📸',
      color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 hover:opacity-90',
      onClick: () => {
        toast.info(language === 'es' 
          ? 'Usa "Crear Imagen" para compartir en Instagram'
          : 'Use "Create Image" to share on Instagram');
        onClose();
      },
      available: true
    },
    {
      name: 'Telegram',
      icon: '✈️',
      color: 'bg-blue-500 hover:bg-blue-600',
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      available: true
    },
    {
      name: 'Email',
      icon: '📧',
      color: 'bg-slate-600 hover:bg-slate-700',
      url: `mailto:?subject=${encodeURIComponent(verse.reference)}&body=${encodedText}`,
      available: true
    },
    {
      name: language === 'es' ? 'Copiar Enlace' : 'Copy Link',
      icon: '🔗',
      color: 'bg-purple-600 hover:bg-purple-700',
      onClick: () => {
        navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        toast.success(language === 'es' ? 'Copiado al portapapeles' : 'Copied to clipboard');
        onClose();
      },
      available: true
    }
  ];

  const handleShare = (platform) => {
    if (platform.onClick) {
      platform.onClick();
      return;
    }

    // Open share URL in new window
    const width = 600;
    const height = 400;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      platform.url,
      'share',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {language === 'es' ? 'Compartir Versículo' : 'Share Verse'}
          </DialogTitle>
        </DialogHeader>

        {/* Verse preview */}
        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {verse.reference}
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300 italic">
            "{verse.text}"
          </p>
        </div>

        {/* Social platform buttons */}
        <div className="grid grid-cols-2 gap-3">
          {socialPlatforms.filter(p => p.available).map((platform) => (
            <Button
              key={platform.name}
              onClick={() => handleShare(platform)}
              className={`${platform.color} text-white h-auto py-3 flex flex-col items-center gap-2`}
            >
              <span className="text-2xl">{platform.icon}</span>
              <span className="text-sm font-medium">{platform.name}</span>
            </Button>
          ))}
        </div>

        {/* Native share (if available on mobile) */}
        {navigator.share && (
          <Button
            onClick={async () => {
              try {
                await navigator.share({
                  title: verse.reference,
                  text: shareText,
                  url: shareUrl,
                });
                onClose();
              } catch (err) {
                // User cancelled or share failed — no action needed
              }
            }}
            variant="outline"
            className="w-full mt-3"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Más opciones de compartir' : 'More share options'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SocialShareDialog;
